<script setup lang="ts">
import { computed } from 'vue';

import { useBrowserStore } from '@apps/main/new-desktop/core/stores';

import { getDomain } from '@libs/utils';
import { AppButton, AppFlicker } from '@libs/ui';

import { useTripSummaryStore } from '../../stores/tripSummaryStore';

const tripSummaryStore = useTripSummaryStore();
const browserStore = useBrowserStore();

const LOCALE_ALLOWED_TO_DISPLAY_INSTALMENT_BANNER = ['sv', 'no'];

const canShowInstalmentBanner = computed(() =>
  LOCALE_ALLOWED_TO_DISPLAY_INSTALMENT_BANNER.includes(
    getDomain.getCurrentDomainSpec().Locale
  )
);
</script>

<template>
  <div class="trip-summary-loader">
    <div>
      <div v-if="browserStore.isLargerThanExtraSmallMax" class="map-loader">
        <AppFlicker
          height="100%"
          width="100%"
          border-radius="border-radius-8"
        />
      </div>
      <div class="products-loader">
        <div
          v-for="(price, index) in tripSummaryStore.tripPriceSegments"
          :key="`${price.title}-${index}`"
          class="product"
        >
          <AppFlicker
            height="spacing-12"
            width="spacing-48"
            border-radius="border-radius-8"
          />
          <div>
            <AppFlicker
              height="spacing-12"
              width="spacing-96"
              border-radius="border-radius-8"
            />
            <AppFlicker
              height="spacing-12"
              width="spacing-40"
              border-radius="border-radius-8"
            />
          </div>
        </div>
      </div>
      <div class="price-pax-calendar-loader">
        <AppFlicker
          height="spacing-16"
          width="spacing-64"
          border-radius="border-radius-12"
        />
        <AppFlicker
          height="spacing-16"
          width="spacing-80"
          border-radius="border-radius-12"
        />
      </div>
    </div>
    <div>
      <div class="banners-loader">
        <div class="banner">
          <div class="banner-container">
            <AppFlicker
              height="spacing-16"
              width="spacing-96"
              border-radius="border-radius-8"
            />
            <AppFlicker
              height="spacing-12"
              width="spacing-40"
              border-radius="border-radius-8"
            />
          </div>
          <div v-if="canShowInstalmentBanner" class="separator" />
          <div v-if="canShowInstalmentBanner" class="banner-container">
            <AppFlicker
              height="spacing-16"
              width="spacing-96"
              border-radius="border-radius-8"
            />
            <AppFlicker
              height="spacing-12"
              width="spacing-40"
              border-radius="border-radius-8"
            />
          </div>
        </div>
      </div>
      <div class="price-loader">
        <AppFlicker
          height="spacing-12"
          width="spacing-64"
          border-radius="border-radius-12"
        />
        <AppFlicker
          height="spacing-16"
          width="spacing-64"
          border-radius="border-radius-12"
        />
      </div>
      <div class="buttons-loader">
        <AppButton
          type="primary"
          size="large"
          state="loadingSpinner"
          :wide="true"
        />
        <AppButton
          type="secondary"
          size="large"
          state="loadingSpinner"
          :wide="true"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.trip-summary-loader {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;

  .map-loader {
    height: 225px;
    margin-bottom: var(--spacing-16);
  }

  .summary-header-loader {
    display: flex;
    gap: var(--spacing-24);
    flex-flow: column;
    margin-bottom: var(--spacing-16);
    padding-bottom: var(--spacing-16);

    .date-loader,
    .pax-loader {
      display: flex;
      flex-direction: column;
      gap: calc(var(--spacing-4) + var(--spacing-2));
    }
  }

  .products-loader {
    display: flex;
    flex-direction: column;
    margin-bottom: var(--spacing-16);

    .product {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      gap: var(--spacing-16);
      &:not(:last-child) {
        border-bottom: 1px solid var(--color-secondary-90);
        padding-bottom: var(--spacing-16);
        margin-bottom: var(--spacing-16);
      }
      > div {
        display: flex;
        justify-content: space-between;
      }
    }
  }

  .price-pax-calendar-loader {
    display: flex;
    flex-direction: row;
    @include for-large-devices-max {
      flex-direction: column;
      justify-content: space-between;
    }
    justify-content: center;
    align-items: center;
    padding: var(--spacing-24) 0;
    gap: var(--spacing-24);
  }

  .banners-loader {
    display: flex;
    flex-direction: column;
    margin-bottom: var(--spacing-24);

    .banner {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-12);
      padding: var(--spacing-12) 0;
      .banner-container {
        display: flex;
        justify-content: space-between;
      }
      .separator {
        border-top: 1px solid var(--color-secondary-90);
      }
    }
  }
  .price-loader {
    display: flex;
    justify-content: space-between;
    padding-bottom: var(--spacing-24);
    @include for-less-or-equal-than-extra-small-max {
      padding-bottom: 0;
    }
  }
  .buttons-loader {
    display: flex;
    flex-flow: column;
    gap: var(--spacing-12);
    margin-top: var(--spacing-8);
    .share-and-save-buttons-wrapper {
      margin-top: var(--spacing-4);
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: var(--spacing-8);
    }
  }
}
</style>
